import { useEffect, useRef, useCallback, useState, type ReactNode } from 'react';

const INACTIVITY_TIMEOUT_MS = 60 * 60 * 1000; // 60 minutes
const ACTIVITY_EVENTS: Array<keyof WindowEventMap> = ['mousemove', 'keydown', 'click', 'touchstart', 'scroll'];

/**
 * Minimal auth store interface expected from the Zustand auth store (task 13.1).
 * For now, we use local state to manage auth status.
 */
export interface AuthState {
  isAuthenticated: boolean;
  idToken: string | null;
  clearSession: () => void;
}

export interface AuthGuardProps {
  children: ReactNode;
  /** Optional external auth state. Falls back to internal state if not provided. */
  authState?: AuthState;
  /** Called when user is redirected to login (e.g., for routing). */
  onUnauthenticated?: () => void;
  /** Inactivity timeout in ms. Defaults to 60 minutes. */
  inactivityTimeout?: number;
}

/**
 * AuthGuard wraps authenticated routes and redirects to login when:
 * - No valid session exists (missing tokens)
 * - The session has been idle for more than 60 minutes
 *
 * Tracks user activity via mousemove, keydown, click, touchstart, and scroll events.
 * Clears tokens and triggers re-authentication on timeout.
 *
 * @see Requirements 7.5, 10.7
 */
export function AuthGuard({
  children,
  authState,
  onUnauthenticated,
  inactivityTimeout = INACTIVITY_TIMEOUT_MS,
}: AuthGuardProps) {
  const lastActivityRef = useRef<number>(Date.now());
  const timeoutIdRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isSessionExpired, setIsSessionExpired] = useState(false);

  const isAuthenticated = authState?.isAuthenticated ?? false;

  const clearSession = useCallback(() => {
    if (authState?.clearSession) {
      authState.clearSession();
    }
    setIsSessionExpired(true);
    onUnauthenticated?.();
  }, [authState, onUnauthenticated]);

  const resetInactivityTimer = useCallback(() => {
    lastActivityRef.current = Date.now();

    if (timeoutIdRef.current) {
      clearTimeout(timeoutIdRef.current);
    }

    timeoutIdRef.current = setTimeout(() => {
      clearSession();
    }, inactivityTimeout);
  }, [inactivityTimeout, clearSession]);

  // Set up activity event listeners
  useEffect(() => {
    if (!isAuthenticated) return;

    const handleActivity = () => {
      resetInactivityTimer();
    };

    // Start the inactivity timer
    resetInactivityTimer();

    ACTIVITY_EVENTS.forEach((event) => {
      window.addEventListener(event, handleActivity, { passive: true });
    });

    return () => {
      ACTIVITY_EVENTS.forEach((event) => {
        window.removeEventListener(event, handleActivity);
      });
      if (timeoutIdRef.current) {
        clearTimeout(timeoutIdRef.current);
      }
    };
  }, [isAuthenticated, resetInactivityTimer]);

  // Check for token presence on mount
  useEffect(() => {
    if (!isAuthenticated && !isSessionExpired) {
      onUnauthenticated?.();
    }
  }, [isAuthenticated, isSessionExpired, onUnauthenticated]);

  if (!isAuthenticated) {
    return null; // Parent handles routing to login screen
  }

  return <>{children}</>;
}
